import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { useDesignVariant } from '../../_designVariant';

/**
 * "Filter display order" card — the display order of the main-filter
 * categories shown in the menu bar (category.order_index, saved via
 * PATCH /admin/navbar-order/{id} on the global Save).
 *
 * Note: HOW the filter values are sorted (ORDER_SIDE_FILTER_BY) lives in the
 * Filters & sorting modal (GroupsFilterModal), not here — this card is purely
 * about the navbar category order.
 *
 * orderRows: category rows ({id, display_name, order_index, …}) for the chosen
 *            main-filter level (NAVBAR_ITEMS_TO_LIST — picked in the
 *            NavigationMenu card); empty when no level is chosen.
 * onOrderRowsChange(nextRows)
 */
const NavbarFilterSortingCard = ({ orderRows = [], onOrderRowsChange, levelChosen = false, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    // doorloop: no box-in-a-box -- the categories are rows like any other
    const { tokens } = useDesignVariant();
    const flat = tokens.row !== 'stack';
    const setOrderIndex = (id, raw) => {
        if (typeof onOrderRowsChange !== 'function') return;
        const v = parseInt(raw, 10);
        onOrderRowsChange(orderRows.map(r => r.id === id ? { ...r, order_index: Number.isNaN(v) ? 0 : v } : r));
    };

    return (
        <ConfigCard
            anchor="cfg-card-nav-sorting"
            title={tr('cfg_nav_sorting_title', 'Filter display order')}
            description={tr('cfg_nav_sorting_desc', 'The order in which the main-filter categories appear in the menu bar.')}
            theme={theme}
        >
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                    {tr('cfg_nav_sorting_order_help', 'Specify the order in which the filters are shown. Lower numbers come first.')}
                </Typography>

                {!levelChosen && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {tr('cfg_nav_sorting_no_level', 'Pick a main-filter category in the Navigation menu card first.')}
                    </Typography>
                )}
                {levelChosen && orderRows.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {tr('cfg_nav_sorting_no_rows', 'No categories found for the chosen main filter.')}
                    </Typography>
                )}
                {orderRows.length > 0 && (
                    <Box sx={flat ? {} : { p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <Stack spacing={flat ? 0 : 0.75}>
                            {orderRows.map((r) => (
                                <Box key={r.id} sx={flat ? {
                                    ...tokens.rowSx,
                                    gridTemplateColumns: 'minmax(0, 1fr) 96px',
                                } : {
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 96px',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.25, py: 0.5,
                                }}>
                                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(flat ? tokens.rowLabelSx : {}) }}>
                                        {r.display_name || r.name || r.id}
                                    </Typography>
                                    <TextField
                                        type="number" size="small"
                                        value={r.order_index ?? 0}
                                        onChange={(e) => setOrderIndex(r.id, e.target.value)}
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>
        </ConfigCard>
    );
};

NavbarFilterSortingCard.propTypes = {
    orderRows:         PropTypes.array,
    onOrderRowsChange: PropTypes.func,
    levelChosen:       PropTypes.bool,
    theme:             PropTypes.string,
    t:                 PropTypes.func,
};

export default NavbarFilterSortingCard;
